import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {useAuth} from '../../../modules/auth'
import {Modal, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import TimeOffFilterDropdown, {TimeOffUserFilterValues} from './TimeOffFilterDropdown'
import toaster from '../../../../Utils/toaster'

const API_URL = process.env.REACT_APP_API_URL
const itemsPerPage = Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE) || 10

type Props = { userId?: string }

type TimeOffRow = {
  id: number
  userId: string
  interpreterName: string
  interpreterEmail: string
  interpreterNumber: string
  startTime: string
  endTime: string
  totalDays: number
  status: string
  notes?: string
  createdDateTime: string
  createdBy?: string
}

const TimeOffView: React.FC<Props> = ({userId}) => {
  const {currentUser} = useAuth()
  const [rows, setRows] = useState<TimeOffRow[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filterData, setFilterData] = useState<TimeOffUserFilterValues>({startDate: '', endDate: '', statuses: []})
  const [formStart, setFormStart] = useState<string>('')
  const [formEnd, setFormEnd] = useState<string>('')
  const [formNotes, setFormNotes] = useState<string>('')
  const canApproveReject = currentUser?.result?.role === 'Admin'
  const uid = userId || currentUser?.result?.code

  const payload = useMemo(() => {
    const statuses = filterData.statuses?.length > 0 ? filterData.statuses : undefined
    return {
      startDate: filterData.startDate ? new Date(filterData.startDate).toISOString() : undefined,
      endDate: filterData.endDate ? new Date(filterData.endDate).toISOString() : undefined,
      statuses,
    }
  }, [filterData])

  const fetchRows = async (page: number) => {
    setIsLoading(true)
    try {
      const res = await axios.post(`${API_URL}/TimeOff/user/${uid}`, payload, {
        params: {page, items_per_page: itemsPerPage, order: 'desc', sort: 'starttime'},
      })
      const {data, payload: meta} = res.data
      setRows(data)
      setTotalPages(meta.pagination.last_page)
      setCurrentPage(page)
    } catch (err) {
      console.error('TimeOffView fetchRows', err)
      toaster('error', 'Failed to load time offs')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => { if (uid) fetchRows(1) }, [uid])
  useEffect(() => { if (uid) fetchRows(1) }, [payload, uid])

  const parseLocalDate = (dateStr: string) => {
    // dateStr: YYYY-MM-DD
    const parts = dateStr.split('-').map(p => Number(p))
    if (parts.length !== 3) return null
    const [y, m, d] = parts
    if (!y || !m || !d) return null
    const dt = new Date(y, m - 1, d, 0, 0, 0, 0) // local midnight
    return Number.isNaN(dt.getTime()) ? null : dt
  }

  const endOfLocalDay = (startOfDay: Date) =>
    new Date(startOfDay.getFullYear(), startOfDay.getMonth(), startOfDay.getDate(), 23, 59, 59, 999)

  const toDateInputValue = (isoOrDate: string) => {
    const dt = new Date(isoOrDate)
    if (Number.isNaN(dt.getTime())) return ''
    const y = dt.getFullYear()
    const m = String(dt.getMonth() + 1).padStart(2, '0')
    const d = String(dt.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  const getTimelineStatus = (endIso: string): 'Past' | 'Upcoming' => {
    const end = new Date(endIso)
    const today = new Date()
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0)
    return end < todayStart ? 'Past' : 'Upcoming'
  }

  const openCreate = () => {
    setEditingId(null)
    setFormStart('')
    setFormEnd('')
    setFormNotes('')
    setShowCreateModal(true)
  }

  const openEdit = (row: TimeOffRow) => {
    setEditingId(row.id)
    setFormStart(toDateInputValue(row.startTime))
    setFormEnd(toDateInputValue(row.endTime))
    setFormNotes(row.notes || '')
    setShowCreateModal(true)
  }

  const createTimeOff = async () => {
    if (!formStart || !formEnd) return
    const startDay = parseLocalDate(formStart)
    const endDayStart = parseLocalDate(formEnd)
    if (!startDay || !endDayStart) {
      toaster('error', 'Invalid date')
      return
    }
    if (endDayStart < startDay) {
      toaster('error', 'End date must be on or after start date')
      return
    }
    const start = startDay
    const end = endOfLocalDay(endDayStart)
    try {
      const res = await axios.post(`${API_URL}/TimeOff/create`, {
        userId: uid,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        notes: formNotes || undefined,
      })
      if (res.data?.status === 'S') {
        toaster('success', 'Time off created')
        setFormNotes('')
        setFormStart('')
        setFormEnd('')
        setShowCreateModal(false)
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to create time off')
      }
    } catch (err) {
      console.error('TimeOffView create', err)
      toaster('error', 'Failed to create time off')
    }
  }

  const updateTimeOff = async (id: number) => {
    if (!formStart || !formEnd) return
    const startDay = parseLocalDate(formStart)
    const endDayStart = parseLocalDate(formEnd)
    if (!startDay || !endDayStart) {
      toaster('error', 'Invalid date')
      return
    }
    if (endDayStart < startDay) {
      toaster('error', 'End date must be on or after start date')
      return
    }
    const start = startDay
    const end = endOfLocalDay(endDayStart)
    try {
      const res = await axios.put(`${API_URL}/TimeOff/${id}`, {
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        notes: formNotes || undefined,
      })
      if (res.data?.status === 'S') {
        toaster('success', 'Time off updated')
        setShowCreateModal(false)
        setEditingId(null)
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to update time off')
      }
    } catch (err) {
      console.error('TimeOffView update', err)
      toaster('error', 'Failed to update time off')
    }
  }

  const deleteRow = async (id: number) => {
    try {
      const res = await axios.delete(`${API_URL}/TimeOff/${id}`)
      if (res.data?.status === 'S') {
        toaster('success', 'Time off deleted')
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to delete time off')
      }
    } catch (err) {
      console.error('TimeOffView delete', err)
      toaster('error', 'Failed to delete time off')
    }
  }

  const approveRow = async (id: number) => {
    try {
      const res = await axios.put(`${API_URL}/TimeOff/approve/${id}`)
      if (res.data?.status === 'S') {
        toaster('success', 'Time off approved')
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to approve time off')
      }
    } catch (err) {
      console.error('TimeOffView approve', err)
      toaster('error', 'Failed to approve time off')
    }
  }
  const rejectRow = async (id: number) => {
    try {
      const res = await axios.put(`${API_URL}/TimeOff/reject/${id}`)
      if (res.data?.status === 'S') {
        toaster('success', 'Time off rejected')
        fetchRows(currentPage)
      } else {
        toaster('error', res.data?.text || 'Failed to reject time off')
      }
    } catch (err) {
      console.error('TimeOffView reject', err)
      toaster('error', 'Failed to reject time off')
    }
  }

  return (
    <div className='card shadow-sm'>
      <div className='card-header d-flex align-items-center px-0'>
        <div className='card-title'>Time Off</div>
        <div className='card-toolbar d-flex align-items-end'>
          <div className='d-flex'>
            <div className='d-flex text-end'>
              <TimeOffFilterDropdown
                initialValues={filterData}
                onApply={(vals) => { setFilterData(vals); setCurrentPage(1) }}
                onReset={() => { setFilterData({startDate:'', endDate:'', statuses:[]}); setCurrentPage(1) }}
              />
            </div>

            <div className='d-flex align-items-end ms-2'>
              <OverlayTrigger placement='top' overlay={<Tooltip id='tooltip-create'>Create</Tooltip>}>
                <button type='button' className='btn btn-sm btn-primary' onClick={openCreate}>
                  <i className='bi bi-calendar-plus me-2'></i>
                  Add Time Off
                </button>
              </OverlayTrigger>
            </div>
          </div>
        </div>
      </div>

      <div className='card-body'>
        <Modal
          show={showCreateModal}
          onHide={() => { setShowCreateModal(false); setEditingId(null) }}
          centered
          backdrop='static'
          keyboard={!isLoading}
        >
          <Modal.Header className='py-3'>
            <Modal.Title className='fw-bold'>{editingId ? 'Edit Time Off' : 'Create Time Off'}</Modal.Title>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              onClick={() => { setShowCreateModal(false); setEditingId(null) }}
              aria-label='Close'
              disabled={isLoading}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2' />
            </button>
          </Modal.Header>
          <Modal.Body>
            <div className='row g-3'>
              <div className='col-12 col-md-6'>
                <label className='form-label fs-7 mb-1'>Time Off Start Date</label>
                <input
                  type='date'
                  className='form-control form-control-sm'
                  value={formStart}
                  onChange={(e) => setFormStart(e.target.value)}
                />
              </div>
              <div className='col-12 col-md-6'>
                <label className='form-label fs-7 mb-1'>Time Off End Date</label>
                <input
                  type='date'
                  className='form-control form-control-sm'
                  value={formEnd}
                  onChange={(e) => setFormEnd(e.target.value)}
                />
              </div>
              <div className='col-12'>
                <label className='form-label fs-7 mb-1'>Notes</label>
                <textarea
                  className='form-control form-control-sm'
                  rows={3}
                  maxLength={250}
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder='Optional notes (max 250 chars)'
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className='py-3'>
            <button
              type='button'
              className='btn btn-sm btn-light'
              onClick={() => { setShowCreateModal(false); setEditingId(null) }}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-sm btn-primary'
              onClick={() => (editingId ? updateTimeOff(editingId) : createTimeOff())}
              disabled={isLoading || !formStart || !formEnd}
            >
              {editingId ? 'Save' : 'Create'}
            </button>
          </Modal.Footer>
        </Modal>

        {!isLoading && rows.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center' style={{minHeight: '45vh'}}>
            <div className='border rounded p-10 text-center bg-white' style={{maxWidth: 560, width: '100%'}}>
              <div className='mb-6'>
                <i className='bi bi-calendar-x text-gray-500' style={{fontSize: '3.5rem'}}></i>
              </div>
              <div className='fs-3 fw-bold mb-2'>No time off added yet.</div>
              <div className='text-muted'>
                Click <span className='fw-semibold'>“Add Time Off”</span> to record your upcoming days off.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className='table-responsive'>
              <table className='table table-row-dashed table-row-gray-300 align-middle gs-2 gy-2 table-hover'>
                <thead>
                  <tr className='fw-semibold text-muted text-uppercase'>
                    <th className='min-w-150px'>Start</th>
                    <th className='min-w-150px'>End</th>
                    <th className='min-w-120px'>Status</th>
                    <th className='min-w-100px'>Total Days</th>
                    <th className='min-w-200px'>Notes</th>
                    <th className='min-w-150px text-end'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{new Date(r.startTime).toLocaleDateString()}</td>
                      <td>{new Date(r.endTime).toLocaleDateString()}</td>
                      <td>
                        {(() => {
                          const s = getTimelineStatus(r.endTime)
                          const cls = s === 'Past' ? 'badge badge-light-danger' : 'badge badge-light-success'
                          return <span className={cls}>{s}</span>
                        })()}
                      </td>
                      <td>{r.totalDays}</td>
                      <td>{r.notes || ''}</td>
                      <td className='text-end'>
                        <button className='btn btn-sm btn-light-primary me-2' onClick={() => openEdit(r)}>
                          Edit
                        </button>
                        <button className='btn btn-sm btn-light-danger me-2' onClick={() => deleteRow(r.id)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className='d-flex justify-content-between align-items-center'>
              <div>Page {currentPage} of {totalPages}</div>
              <div>
                <button className='btn btn-sm btn-light me-2' disabled={currentPage<=1} onClick={()=>fetchRows(currentPage-1)}>Prev</button>
                <button className='btn btn-sm btn-light' disabled={currentPage>=totalPages} onClick={()=>fetchRows(currentPage+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TimeOffView

